/**
 * Venda.jsx — PDV
 *
 * Novidades:
 * ── Ao finalizar: pergunta se quer vincular a um sub-cliente (lista do /sub-clientes)
 * ── Sub-cliente aparece no comprovante
 * ── Botão "Ocultar valores" no comprovante (oculta preços unitários mas mantém totais)
 * ── Link "Histórico" na barra de atalhos → /historico-vendas
 *
 * Atalhos globais:
 *   F2    → busca  |  F10  → todos  |  F12 → finalizar
 *   Del   → limpar |  F3   → digitar qtd  |  ↑↓ → navegar
 */

import {
  useEffect,
  useState,
  useContext,
  useCallback,
  useMemo,
  useRef,
  memo,
} from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { AuthContext } from "../../context/AuthContext";
import api from "../../api/api";
import { pdf } from "@react-pdf/renderer";
import ComprovantePDF from "./ComprovantePDF";
import { saveAs } from "file-saver";
import "./Venda.scss";

// ─── Constantes ───────────────────────────────────────────────────────────────

const FORMAS_PAGAMENTO = ["Dinheiro", "Cartão Crédito", "Cartão Débito", "Pix"];
const PODE_DESCONTO = ["admin", "superadmin"];
const FORMATOS_LABEL = {
  a4: "A4 — e-mail / WhatsApp / arquivo",
  cupom: "Cupom 80mm — estilo nota fiscal",
  bobina: "Bobina térmica — impressora / Zebra",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const arredondar = (v) => Math.round((v + Number.EPSILON) * 100) / 100;
const formatBRL = (v) =>
  Number(v ?? 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
const resolverFmt = (c) => c?.tipo_impressao ?? null;

const normalizarProdutoEstoque = (p) => ({
  _key: `estoque_${p.id}`,
  _tipo: "produto",
  estoque_id: p.id,
  produto_id: p.produto_id,
  produto_nome: p.produto_nome,
  ean: p.ean,
  imagem_url: p.imagem_url ?? null,
  marca: p.marca ?? null,
  valor_venda: parseFloat(p.valor_venda),
  _estoque_max: p.quantidade,
  quantidade: 1,
});

const normalizarServico = (s) => ({
  _key: `servico_${s.id}`,
  _tipo: "servico",
  estoque_id: `srv_${s.id}`,
  produto_id: s.id,
  produto_nome: s.nome,
  ean: null,
  imagem_url: s.imagem_url ?? null,
  marca: s.marca ?? null,
  valor_venda: parseFloat(s.preco_custo),
  _estoque_max: Infinity,
  quantidade: 1,
});

// ─── Sub-componentes ──────────────────────────────────────────────────────────

const Kbd = ({ children }) => (
  <kbd className="kbd" aria-hidden="true">
    {children}
  </kbd>
);

const AtalhosBar = memo(() => (
  <div className="atalhos-bar" role="note" aria-label="Atalhos de teclado">
    <span className="atalhos-bar__item">
      <Kbd>F2</Kbd> Buscar
    </span>
    <span className="atalhos-bar__item">
      <Kbd>F10</Kbd> Todos
    </span>
    <span className="atalhos-bar__item">
      <Kbd>Del</Kbd> Limpar
    </span>
    <span className="atalhos-bar__item">
      <Kbd>F12</Kbd> Finalizar
    </span>
    <span className="atalhos-bar__item">
      <Kbd>↑↓</Kbd> Navegar
    </span>
    <span
      className="atalhos-bar__item atalhos-bar__item--divider"
      aria-hidden="true"
    >
      |
    </span>
    <span className="atalhos-bar__item atalhos-bar__item--qty">
      <Kbd>+</Kbd> add qtd
    </span>
    <span className="atalhos-bar__item atalhos-bar__item--qty">
      <Kbd>−</Kbd> red qtd
    </span>
    <span className="atalhos-bar__item atalhos-bar__item--qty">
      <Kbd>F3</Kbd> digitar qtd
    </span>
    <span
      className="atalhos-bar__item atalhos-bar__item--divider"
      aria-hidden="true"
    >
      |
    </span>
    <Link
      to="/historico-vendas"
      className="atalhos-bar__link"
      title="Histórico de vendas"
    >
      Histórico
    </Link>
  </div>
));
AtalhosBar.displayName = "AtalhosBar";

const SugestaoItem = memo(
  ({ item, onAdd, isFocused, itemRef, onKeyDown, mostrarImagem }) => {
    const isServico = item._tipo === "servico";
    const semEstoque =
      !isServico && (!item.valor_venda || item._estoque_max <= 0);

    return (
      <button
        ref={itemRef}
        type="button"
        className={`sugestao-item${isFocused ? " sugestao-item--focused" : ""}${isServico ? " sugestao-item--servico" : ""}`}
        onClick={() => onAdd(item)}
        onKeyDown={onKeyDown}
        disabled={semEstoque}
        role="option"
        aria-selected={isFocused}
        tabIndex={-1}
      >
        {/* Thumbnail no dropdown */}
        {mostrarImagem && (
          <span className="sugestao-item__thumb">
            {item.imagem_url ? (
              <img
                src={item.imagem_url}
                alt={item.produto_nome}
                className="sugestao-item__thumb-img"
              />
            ) : (
              <span
                className="sugestao-item__thumb-placeholder"
                aria-hidden="true"
              >
                📦
              </span>
            )}
          </span>
        )}
        <span className="sugestao-item__nome">
          {item.produto_nome}
          {isServico && <span className="sugestao-item__tag">serviço</span>}
        </span>
        {item.marca && (
          <span className="sugestao-item__marca">{item.marca}</span>
        )}

        <span className="sugestao-item__estoque">
          {isServico
            ? "Mão de obra"
            : item._estoque_max > 0
              ? `${item._estoque_max} un`
              : "Sem estoque"}
        </span>
        <span className="sugestao-item__preco">
          {item.valor_venda ? formatBRL(item.valor_venda) : "Sem preço"}
        </span>
      </button>
    );
  },
);
SugestaoItem.displayName = "SugestaoItem";

const ItemRow = memo(
  ({
    item,
    onRemover,
    onAlterarQtd,
    onIncrementar,
    onDecrementar,
    onDigitarQtd,
    qtyRef,
    mostrarImagem,
  }) => {
    const isServico = item._tipo === "servico";
    const maxQtd = item._estoque_max ?? Infinity;
    const total = arredondar(item.valor_venda * item.quantidade);

    const handleKeyDown = (e) => {
      switch (e.key) {
        case "Delete":
          if (item.quantidade === 1) {
            e.preventDefault();
            onRemover(item.estoque_id);
          }
          break;
        case "+":
        case "Add":
          e.preventDefault();
          onIncrementar(item.estoque_id, maxQtd);
          break;
        case "-":
        case "Subtract":
          e.preventDefault();
          onDecrementar(item.estoque_id);
          break;
        case "F3":
          e.preventDefault();
          onDigitarQtd(
            item.estoque_id,
            item.produto_nome,
            item.quantidade,
            maxQtd,
          );
          break;
        default:
          break;
      }
    };

    return (
      <tr
        className={`${item._flash ? "item-row--flash" : ""}${isServico ? " item-row--servico" : ""}`}
      >
        {/* Coluna de imagem — condicional */}
        {mostrarImagem && (
          <td data-label="Foto" className="cell--foto">
            {item.imagem_url ? (
              <img
                src={item.imagem_url}
                alt={item.produto_nome}
                className="item-thumb"
              />
            ) : (
              <span className="item-thumb-placeholder" aria-hidden="true">
                📦
              </span>
            )}
          </td>
        )}

        <td data-label="EAN" className="cell--ean">
          {isServico ? (
            <span className="servico-tag">serviço</span>
          ) : (
            item.ean || "-"
          )}
        </td>
        <td data-label="Produto" className="cell--produto">
          {item.produto_nome}
          {item.marca && <span className="cell--marca">{item.marca}</span>}
        </td>
        <td data-label="Qtd">
          <div className="qty-control">
            <button
              type="button"
              className="qty-control__btn"
              tabIndex={-1}
              onClick={() => onDecrementar(item.estoque_id)}
              disabled={item.quantidade <= 1}
              aria-label={`Reduzir quantidade de ${item.produto_nome}`}
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
            <input
              ref={qtyRef}
              type="number"
              className="qty-input"
              value={item.quantidade}
              min={1}
              max={isServico ? undefined : maxQtd}
              aria-label={`Quantidade de ${item.produto_nome}`}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (v > 0 && (isServico || v <= maxQtd))
                  onAlterarQtd(item.estoque_id, v);
              }}
              onKeyDown={handleKeyDown}
            />
            <button
              type="button"
              className="qty-control__btn"
              tabIndex={-1}
              onClick={() => onIncrementar(item.estoque_id, maxQtd)}
              disabled={!isServico && item.quantidade >= maxQtd}
              aria-label={`Aumentar quantidade de ${item.produto_nome}`}
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          </div>
        </td>
        <td data-label="Unit." className="cell--price">
          {formatBRL(item.valor_venda)}
        </td>
        <td data-label="Total" className="cell--total">
          {formatBRL(total)}
        </td>
        <td data-label="Ações" className="cell--actions">
          <button
            type="button"
            className="btn-remover"
            tabIndex={-1}
            onClick={() => onRemover(item.estoque_id)}
            aria-label={`Remover ${item.produto_nome}`}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </td>
      </tr>
    );
  },
);
ItemRow.displayName = "ItemRow";

const CarrinhoVazio = () => (
  <tr>
    <td colSpan={6}>
      <div className="carrinho-vazio" role="status" aria-live="polite">
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.3"
        >
          <circle cx="9" cy="21" r="1" />
          <circle cx="20" cy="21" r="1" />
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
        </svg>
        <p>Nenhum item adicionado à venda.</p>
        <span>
          Pressione <Kbd>F2</Kbd> para buscar ou <Kbd>F10</Kbd> para ver todos.
        </span>
      </div>
    </td>
  </tr>
);

// ─── Componente Principal ─────────────────────────────────────────────────────

const Venda = () => {
  const { user } = useContext(AuthContext);
  const clienteId = useMemo(
    () => JSON.parse(localStorage.getItem("usuario"))?.cliente_id,
    [],
  );

  // Refs
  const searchInputRef = useRef(null);
  const searchWrapRef = useRef(null);
  const dropdownRef = useRef(null);
  const finalizarBtnRef = useRef(null);
  const qtyRefs = useRef({});
  const sugestaoRefs = useRef([]);
  const itemFocadoRef = useRef(null);
  const limparVendaRef = useRef(null);
  const buscaCompletaRef = useRef(null);
  const incrementarQtdRef = useRef(null);
  const decrementarQtdRef = useRef(null);
  const ultimoItemF3Ref = useRef(null);

  // State
  const [sugestaoFocada, setSugestaoFocada] = useState(-1);
  const [mostrarImagens, setMostrarImagens] = useState(true);
  const [estoque, setEstoque] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [subClientes, setSubClientes] = useState([]);
  const [search, setSearch] = useState("");
  const [dropdownOpen, setDropdown] = useState(false);
  const [cliente, setCliente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [templatePadrao, setTemplatePadrao] = useState(null);

  // Rascunho
  const [itensVenda, setItensVenda] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("pdv_itens") ?? "[]");
    } catch {
      return [];
    }
  });
  const [desconto, setDesconto] = useState(() => {
    try {
      return localStorage.getItem("pdv_desconto") ?? "";
    } catch {
      return "";
    }
  });

  // Toggle ocultar valores no comprovante
  const [ocultarValoresComprovante, setOcultarValoresComprovante] =
    useState(false);

  const podeDesconto = PODE_DESCONTO.includes(user?.nivel_acesso);

  // ── Fetch inicial ───────────────────────────────────────────────────────────
  // ── Fetch inicial ───────────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      try {
        const [
          estoqueRes,
          servicosRes,
          clienteRes,
          subClientesRes,
          templatesRes,
        ] = await Promise.all([
          api.get("/estoque"),
          api.get("/produtos", { params: { tipo: "servico" } }),
          api.get("/clientes").catch(() => ({ data: null })),
          api.get("/sub-clientes").catch(() => ({ data: [] })),
          api
            .get(`/clientes/${clienteId}/templates`)
            .catch(() => ({ data: null })),
        ]);
        setEstoque(estoqueRes.data);
        setServicos(
          (servicosRes.data ?? [])
            .filter((s) => s.ativo)
            .map(normalizarServico),
        );
        setCliente(clienteRes.data);
        setSubClientes(subClientesRes.data ?? []);

        // ↓↓↓ ADICIONAR AQUI, logo após o setSubClientes ↓↓↓
        const templates = templatesRes.data?.templates ?? [];
        const padrao =
          templates.find(
            (t) => t.tipo === "recibo_venda" && t.is_padrao_categoria === true,
          ) ?? null;
        setTemplatePadrao(padrao);
      } catch (err) {
        console.error("[Venda] init:", err);
      } finally {
        setLoading(false);
        setTimeout(() => searchInputRef.current?.focus(), 200);
      }
    };
    init();
  }, []);

  // ── Sugestões ──────────────────────────────────────────────────────────────
  const sugestoes = useMemo(() => {
    if (!search.trim()) return [];
    const t = search.toLowerCase();
    const matchProdutos = estoque
      .filter(
        (p) =>
          p.produto_nome.toLowerCase().includes(t) ||
          (p.ean && p.ean.includes(t)),
      )
      .map((p) => ({
        ...normalizarProdutoEstoque(p),
        _estoque_max: p.quantidade,
      }))
      .slice(0, 6);
    const matchServicos = servicos
      .filter((s) => s.produto_nome.toLowerCase().includes(t))
      .slice(0, 4);
    return [...matchProdutos, ...matchServicos];
  }, [search, estoque, servicos]);

  useEffect(() => {
    setSugestaoFocada(-1);
    sugestaoRefs.current = [];
  }, [sugestoes]);

  // ── Cálculos ────────────────────────────────────────────────────────────────
  const subtotal = useMemo(
    () => itensVenda.reduce((acc, i) => acc + i.valor_venda * i.quantidade, 0),
    [itensVenda],
  );
  const descontoNum = useMemo(() => {
    const v = parseFloat(desconto);
    return isNaN(v) || v < 0 ? 0 : Math.min(v, subtotal);
  }, [desconto, subtotal]);
  const total = useMemo(
    () => arredondar(subtotal - descontoNum),
    [subtotal, descontoNum],
  );

  // ── Persiste rascunho ───────────────────────────────────────────────────────
  useEffect(() => {
    try {
      localStorage.setItem(
        "pdv_itens",
        JSON.stringify(itensVenda.map(({ _flash, ...r }) => r)),
      );
    } catch {}
  }, [itensVenda]);
  useEffect(() => {
    try {
      desconto
        ? localStorage.setItem("pdv_desconto", desconto)
        : localStorage.removeItem("pdv_desconto");
    } catch {}
  }, [desconto]);

  // ── Fecha dropdown fora ─────────────────────────────────────────────────────
  useEffect(() => {
    const h = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        searchWrapRef.current &&
        !searchWrapRef.current.contains(e.target)
      ) {
        setDropdown(false);
        setSugestaoFocada(-1);
      }
    };
    document.addEventListener("mousedown", h);
    document.addEventListener("touchstart", h, { passive: true });
    return () => {
      document.removeEventListener("mousedown", h);
      document.removeEventListener("touchstart", h);
    };
  }, []);

  // ── Atalhos globais ─────────────────────────────────────────────────────────
  useEffect(() => {
    const h = (e) => {
      if (document.querySelector(".swal2-container")) return;
      switch (e.key) {
        case "F2":
          e.preventDefault();
          searchInputRef.current?.focus();
          searchInputRef.current?.select();
          break;
        case "F10":
          e.preventDefault();
          buscaCompletaRef.current?.();
          break;
        case "F12":
          e.preventDefault();
          finalizarBtnRef.current?.click();
          break;
        case "F3":
          e.preventDefault();
          ultimoItemF3Ref.current?.();
          break;
        case "Delete":
          if (
            !["INPUT", "SELECT", "TEXTAREA"].includes(
              document.activeElement?.tagName,
            )
          ) {
            e.preventDefault();
            limparVendaRef.current?.();
          }
          break;
        case "+":
        case "Add":
          if (
            document.activeElement?.classList.contains("qty-input") &&
            itemFocadoRef.current
          ) {
            e.preventDefault();
            incrementarQtdRef.current?.(itemFocadoRef.current);
          }
          break;
        case "-":
        case "Subtract":
          if (
            document.activeElement?.classList.contains("qty-input") &&
            itemFocadoRef.current
          ) {
            e.preventDefault();
            decrementarQtdRef.current?.(itemFocadoRef.current);
          }
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  // ── Navegação teclado busca ─────────────────────────────────────────────────
  const handleSearchKeyDown = useCallback(
    (e) => {
      if (!dropdownOpen || !sugestoes.length) return;
      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          const n = Math.min(sugestaoFocada + 1, sugestoes.length - 1);
          setSugestaoFocada(n);
          sugestaoRefs.current[n]?.focus();
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          if (sugestaoFocada <= 0) {
            setSugestaoFocada(-1);
            searchInputRef.current?.focus();
          } else {
            const p = sugestaoFocada - 1;
            setSugestaoFocada(p);
            sugestaoRefs.current[p]?.focus();
          }
          break;
        }
        case "Enter": {
          e.preventDefault();
          if (sugestaoFocada >= 0 && sugestoes[sugestaoFocada])
            adicionarItem(sugestoes[sugestaoFocada]);
          else if (sugestoes.length === 1) adicionarItem(sugestoes[0]);
          break;
        }
        case "Escape":
          e.preventDefault();
          setDropdown(false);
          setSugestaoFocada(-1);
          break;
        case "Tab":
          setDropdown(false);
          setSugestaoFocada(-1);
          break;
        default:
          break;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [dropdownOpen, sugestoes, sugestaoFocada],
  );

  const handleSugestaoKeyDown = useCallback(
    (e, i) => {
      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          const n = Math.min(i + 1, sugestoes.length - 1);
          setSugestaoFocada(n);
          sugestaoRefs.current[n]?.focus();
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          if (i === 0) {
            setSugestaoFocada(-1);
            searchInputRef.current?.focus();
          } else {
            setSugestaoFocada(i - 1);
            sugestaoRefs.current[i - 1]?.focus();
          }
          break;
        }
        case "Escape":
          e.preventDefault();
          setDropdown(false);
          setSugestaoFocada(-1);
          searchInputRef.current?.focus();
          break;
        default:
          break;
      }
    },
    [sugestoes.length],
  );

  // ── Adicionar item ──────────────────────────────────────────────────────────
  const adicionarItem = useCallback(
    (item) => {
      if (!item.valor_venda) {
        Swal.fire({
          title: "Sem preço",
          text: "Item sem valor.",
          icon: "warning",
          timer: 1800,
          showConfirmButton: false,
        });
        return;
      }
      if (item._tipo === "produto" && item._estoque_max <= 0) {
        Swal.fire({
          title: "Sem estoque",
          text: "Produto sem estoque disponível.",
          icon: "warning",
          timer: 1800,
          showConfirmButton: false,
        });
        return;
      }
      setItensVenda((prev) => {
        const existe = prev.find((i) => i.estoque_id === item.estoque_id);
        if (existe) {
          if (item._tipo === "servico")
            return prev.map((i) =>
              i.estoque_id === item.estoque_id
                ? {
                    ...i,
                    quantidade: i.quantidade + 1,
                    imagem_url: item.imagem_url,
                    marca: item.marca,
                  }
                : i,
            );
          const estoqueAtual = estoque.find((e) => e.id === item.estoque_id);
          const maxQtd = estoqueAtual?.quantidade ?? 0;
          if (existe.quantidade >= maxQtd) {
            Swal.fire({
              title: "Limite atingido",
              text: `Estoque: ${maxQtd} un`,
              icon: "warning",
              timer: 1800,
              showConfirmButton: false,
            });
            return prev;
          }
          return prev.map((i) =>
            i.estoque_id === item.estoque_id
              ? { ...i, quantidade: i.quantidade + 1 }
              : i,
          );
        }
        return [...prev, { ...item, quantidade: 1 }];
      });
      setSearch("");
      setDropdown(false);
      setSugestaoFocada(-1);
      setTimeout(() => searchInputRef.current?.focus(), 50);
    },
    [estoque],
  );

  const removerItem = useCallback((estoque_id) => {
    setItensVenda((prev) => {
      const idx = prev.findIndex((i) => i.estoque_id === estoque_id);
      const nova = prev.filter((i) => i.estoque_id !== estoque_id);
      setTimeout(() => {
        const ids = nova.map((i) => i.estoque_id);
        const nextId = ids[Math.max(0, idx - 1)];
        if (nova.length > 0 && nextId && qtyRefs.current[nextId])
          qtyRefs.current[nextId]?.focus();
        else searchInputRef.current?.focus();
      }, 50);
      return nova;
    });
  }, []);

  const alterarQuantidade = useCallback((estoque_id, quantidade) => {
    if (quantidade < 1) return;
    setItensVenda((prev) =>
      prev.map((i) => (i.estoque_id === estoque_id ? { ...i, quantidade } : i)),
    );
  }, []);

  // ── Quantidade atalhos ──────────────────────────────────────────────────────
  const flashItem = useCallback((estoque_id) => {
    setItensVenda((prev) =>
      prev.map((i) =>
        i.estoque_id === estoque_id ? { ...i, _flash: true } : i,
      ),
    );
    setTimeout(
      () =>
        setItensVenda((prev) =>
          prev.map((i) =>
            i.estoque_id === estoque_id ? { ...i, _flash: false } : i,
          ),
        ),
      200,
    );
  }, []);

  const incrementarQtd = useCallback(
    (estoque_id, maxQtd = Infinity) => {
      setItensVenda((prev) => {
        const item = prev.find((i) => i.estoque_id === estoque_id);
        if (!item) return prev;
        if (item._tipo !== "servico" && item.quantidade >= maxQtd) {
          Swal.fire({
            title: "Limite atingido",
            text: `Máximo: ${maxQtd} un`,
            icon: "warning",
            timer: 1500,
            showConfirmButton: false,
          });
          return prev;
        }
        return prev.map((i) =>
          i.estoque_id === estoque_id
            ? { ...i, quantidade: i.quantidade + 1 }
            : i,
        );
      });
      flashItem(estoque_id);
      setTimeout(() => qtyRefs.current[estoque_id]?.focus(), 30);
    },
    [flashItem],
  );

  const decrementarQtd = useCallback(
    (estoque_id) => {
      setItensVenda((prev) => {
        const item = prev.find((i) => i.estoque_id === estoque_id);
        if (!item) return prev;
        if (item.quantidade <= 1) {
          const idx = prev.findIndex((i) => i.estoque_id === estoque_id);
          const nova = prev.filter((i) => i.estoque_id !== estoque_id);
          setTimeout(() => {
            const ids = nova.map((i) => i.estoque_id);
            const nextId = ids[Math.max(0, idx - 1)];
            if (nova.length > 0 && nextId && qtyRefs.current[nextId])
              qtyRefs.current[nextId]?.focus();
            else searchInputRef.current?.focus();
          }, 50);
          return nova;
        }
        return prev.map((i) =>
          i.estoque_id === estoque_id
            ? { ...i, quantidade: i.quantidade - 1 }
            : i,
        );
      });
      flashItem(estoque_id);
      setTimeout(() => qtyRefs.current[estoque_id]?.focus(), 30);
    },
    [flashItem],
  );

  const abrirDialogoQtd = useCallback(
    async (estoque_id, nome, qtdAtual, maxQtd) => {
      const { value: novaQtd } = await Swal.fire({
        title: "Quantidade",
        html: `<p style="font-size:14px;font-weight:600;margin-bottom:4px">${nome}</p>${maxQtd !== Infinity ? `<p style="font-size:12px;color:var(--color-text-muted)">Estoque: <b>${maxQtd} un</b></p>` : ""}`,
        input: "number",
        inputAttributes: {
          min: 1,
          max: maxQtd === Infinity ? undefined : maxQtd,
          step: 1,
        },
        inputValue: qtdAtual,
        showCancelButton: true,
        confirmButtonText: "Confirmar ↵",
        cancelButtonText: "Cancelar",
        allowEnterKey: true,
        inputValidator: (v) => {
          const n = parseInt(v, 10);
          if (!v || isNaN(n) || n < 1) return "Informe uma quantidade válida!";
          if (maxQtd !== Infinity && n > maxQtd)
            return `Máximo disponível: ${maxQtd} un`;
        },
        didOpen: () => Swal.getInput()?.select(),
      });
      if (!novaQtd) {
        setTimeout(() => qtyRefs.current[estoque_id]?.focus(), 80);
        return;
      }
      setItensVenda((prev) =>
        prev.map((i) =>
          i.estoque_id === estoque_id
            ? { ...i, quantidade: parseInt(novaQtd, 10) }
            : i,
        ),
      );
      flashItem(estoque_id);
      setTimeout(() => qtyRefs.current[estoque_id]?.focus(), 80);
    },
    [flashItem],
  );

  useEffect(() => {
    incrementarQtdRef.current = incrementarQtd;
  }, [incrementarQtd]);
  useEffect(() => {
    decrementarQtdRef.current = decrementarQtd;
  }, [decrementarQtd]);
  useEffect(() => {
    ultimoItemF3Ref.current = () => {
      if (!itensVenda.length) return;
      const u = itensVenda[itensVenda.length - 1];
      const ea = estoque.find((e) => e.id === u.estoque_id);
      const mq =
        u._tipo === "servico" ? Infinity : (ea?.quantidade ?? Infinity);
      qtyRefs.current[u.estoque_id]?.focus();
      qtyRefs.current[u.estoque_id]?.select();
      setTimeout(
        () => abrirDialogoQtd(u.estoque_id, u.produto_nome, u.quantidade, mq),
        40,
      );
    };
  }, [itensVenda, estoque, abrirDialogoQtd]);

  const limparVenda = useCallback(async () => {
    if (!itensVenda.length) return;
    const { isConfirmed } = await Swal.fire({
      title: "Limpar venda?",
      text: "Todos os itens serão removidos.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, limpar",
      cancelButtonText: "Cancelar",
      allowEnterKey: true,
    });
    if (isConfirmed) {
      setItensVenda([]);
      setDesconto("");
      try {
        localStorage.removeItem("pdv_itens");
        localStorage.removeItem("pdv_desconto");
      } catch {}
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [itensVenda.length]);
  useEffect(() => {
    limparVendaRef.current = limparVenda;
  }, [limparVenda]);

  // ── Busca completa ──────────────────────────────────────────────────────────
  const abrirBuscaCompleta = useCallback(() => {
    const todosProdutos = estoque.map((p) => ({
      ...normalizarProdutoEstoque(p),
      _estoque_max: p.quantidade,
    }));
    const todosItens = [...todosProdutos, ...servicos];
    Swal.fire({
      title: "Buscar produto ou serviço",
      html: `<input type="text" id="swal-filtro" placeholder="Filtrar por nome, EAN…" class="swal2-input" style="margin-bottom:8px;"/>
            <div id="swal-lista" role="listbox" style="max-height:320px;overflow-y:auto;text-align:left;border:1px solid var(--color-border);border-radius:8px;margin-top:4px;"></div>`,
      showConfirmButton: false,
      showCancelButton: true,
      cancelButtonText: "Fechar (Esc)",
      didOpen: () => {
        const input = Swal.getPopup().querySelector("#swal-filtro");
        const lista = Swal.getPopup().querySelector("#swal-lista");
        let focusIdx = -1;
        const getBotoes = () =>
          Array.from(lista.querySelectorAll("button[data-id]:not(:disabled)"));
        const highlight = (bs, i) =>
          bs.forEach((b, j) => {
            b.style.background =
              j === i ? "var(--color-table-row-hover)" : "none";
          });
        const renderLista = (filtro = "") => {
          const t = filtro.toLowerCase();
          const items = todosItens.filter(
            (p) =>
              p.produto_nome.toLowerCase().includes(t) ||
              (p.ean && p.ean.includes(t)),
          );
          lista.innerHTML = items.length
            ? items
                .map((p) => {
                  const isS = p._tipo === "servico";
                  const dis = !isS && (!p.valor_venda || p._estoque_max <= 0);
                  const infoDir = isS
                    ? `<span style="font-size:11px;background:rgba(59,126,255,.12);color:#5b9bff;padding:1px 7px;border-radius:100px;font-weight:600">serviço</span>`
                    : `<span style="font-size:11px;color:var(--color-text-muted)">${p._estoque_max} un</span>`;
                  return `<button data-id="${p.estoque_id}" data-tipo="${p._tipo}" role="option" tabindex="-1" style="display:flex;align-items:center;justify-content:space-between;width:100%;padding:10px 14px;background:none;border:none;border-bottom:1px solid var(--color-border);cursor:pointer;font-family:inherit;color:var(--color-text-primary);outline:none;${dis ? "opacity:.45;cursor:not-allowed;" : ""}" ${dis ? "disabled" : ""}><span style="font-weight:600;font-size:13px;flex:1;text-align:left">${p.produto_nome}</span><span style="margin:0 12px">${infoDir}</span><span style="font-weight:700;color:var(--color-primary);font-size:13px;white-space:nowrap">${p.valor_venda ? formatBRL(p.valor_venda) : "Sem preço"}</span></button>`;
                })
                .join("")
            : `<p style="padding:20px;text-align:center;color:var(--color-text-muted);font-size:13px">Nenhum item encontrado</p>`;
          focusIdx = -1;
          getBotoes().forEach((el) => {
            el.addEventListener("click", () => {
              const item = todosItens.find(
                (x) => String(x.estoque_id) === el.dataset.id,
              );
              if (item) {
                adicionarItem(item);
                Swal.close();
              }
            });
          });
        };
        input.addEventListener("keydown", (e) => {
          const bs = getBotoes();
          if (!bs.length) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            focusIdx = Math.min(focusIdx + 1, bs.length - 1);
            highlight(bs, focusIdx);
            bs[focusIdx]?.focus();
          } else if (e.key === "ArrowUp" && focusIdx > 0) {
            e.preventDefault();
            focusIdx = Math.max(0, focusIdx - 1);
            highlight(bs, focusIdx);
            bs[focusIdx]?.focus();
          } else if (e.key === "Enter") {
            e.preventDefault();
            if (focusIdx >= 0) bs[focusIdx]?.click();
            else if (bs.length === 1) bs[0]?.click();
          }
        });
        lista.addEventListener("keydown", (e) => {
          const bs = getBotoes();
          if (e.key === "ArrowDown") {
            e.preventDefault();
            focusIdx = Math.min(focusIdx + 1, bs.length - 1);
            highlight(bs, focusIdx);
            bs[focusIdx]?.focus();
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            if (focusIdx <= 0) {
              focusIdx = -1;
              input.focus();
            } else {
              focusIdx--;
              highlight(bs, focusIdx);
              bs[focusIdx]?.focus();
            }
          } else if (e.key === "Enter") {
            e.preventDefault();
            document.activeElement?.click();
          }
        });
        renderLista();
        input.addEventListener("input", (e) => renderLista(e.target.value));
        input.focus();
      },
    });
  }, [estoque, servicos, adicionarItem]);
  useEffect(() => {
    buscaCompletaRef.current = abrirBuscaCompleta;
  }, [abrirBuscaCompleta]);

  // ── Solicitar pagamento ─────────────────────────────────────────────────────
  const solicitarPagamento = useCallback(async (totalRestante) => {
    const { value: forma } = await Swal.fire({
      title: "Forma de pagamento",
      html: `<p style="font-size:16px;font-weight:700;margin-bottom:2px">${formatBRL(totalRestante)}</p><p style="font-size:11px;color:var(--color-text-muted)">a pagar · Enter confirma</p>`,
      input: "select",
      inputOptions: Object.fromEntries(FORMAS_PAGAMENTO.map((f) => [f, f])),
      showCancelButton: true,
      confirmButtonText: "Próximo →",
      cancelButtonText: "Cancelar",
      allowEnterKey: true,
    });
    if (!forma) return null;
    const { value: valor } = await Swal.fire({
      title: forma,
      html: `<p style="font-size:16px;font-weight:700;margin-bottom:2px">${formatBRL(totalRestante)}</p><p style="font-size:11px;color:var(--color-text-muted)">a pagar · Enter confirma</p>`,
      input: "number",
      inputAttributes: { min: 0.01, step: "0.01" },
      inputValue: totalRestante.toFixed(2),
      showCancelButton: true,
      confirmButtonText: "Confirmar ↵",
      cancelButtonText: "Voltar",
      allowEnterKey: true,
      inputValidator: (v) =>
        !v || parseFloat(v) <= 0 ? "Informe um valor válido!" : undefined,
      didOpen: () => Swal.getInput()?.select(),
    });
    if (!valor) return null;
    return { forma, valor: parseFloat(valor) };
  }, []);

  // ── Gerar comprovante ───────────────────────────────────────────────────────
  const gerarComprovante = useCallback(
    async (
      pagamentosArray,
      itensFechados,
      trocoValor,
      descontoFechado,
      subClienteObj,
    ) => {
      // ── Resolve formato ──────────────────────────────────────────────────────
      let formato = null;
      let autoImprimir = false;

      if (templatePadrao) {
        // Usa o template padrão de recibo definido nas configurações
        formato = templatePadrao.categoria; // "bobina_80mm" ou "a4"
        autoImprimir = true;
      } else {
        // Fallback: config legada do cliente
        formato = resolverFmt(cliente);
        autoImprimir = Boolean(cliente?.auto_imprimir) && Boolean(formato);
      }

      if (!autoImprimir) {
        const { value: fmt } = await Swal.fire({
          title: "Formato do comprovante",
          input: "select",
          inputOptions: FORMATOS_LABEL,
          inputValue: formato ?? "a4",
          showCancelButton: true,
          confirmButtonText: "Gerar PDF ↵",
          cancelButtonText: "Cancelar",
          allowEnterKey: true,
        });
        if (!fmt) return;
        formato = fmt;
      }

      const vendaData = {
        estabelecimento: cliente?.nome ?? "FluxoEasy",
        itens: itensFechados,
        pagamentos: pagamentosArray,
        troco: trocoValor,
        desconto: descontoFechado,
        subCliente: subClienteObj ?? null,
        ocultarValores: ocultarValoresComprovante,
        template: templatePadrao ?? null, // passa o template para o PDF usar o XML
      };

      try {
        const blob = await pdf(
          <ComprovantePDF
            venda={vendaData}
            cliente={cliente}
            formato={formato}
          />,
        ).toBlob();
        saveAs(blob, `comprovante_${formato}_${Date.now()}.pdf`);
      } catch (err) {
        console.error("[Venda] PDF:", err);
        Swal.fire("Erro", "Não foi possível gerar o comprovante.", "error");
      }
    },
    [cliente, templatePadrao, ocultarValoresComprovante],
  );

  // ── Finalizar venda ─────────────────────────────────────────────────────────
  const finalizarVenda = useCallback(async () => {
    if (!itensVenda.length) {
      Swal.fire("Atenção", "Nenhum item na venda.", "warning");
      return;
    }

    for (const item of itensVenda) {
      if (item._tipo === "servico") continue;
      const disponivel = estoque.find((p) => p.id === item.estoque_id);
      if (!disponivel) {
        Swal.fire(
          "Erro",
          `${item.produto_nome}: produto não encontrado.`,
          "error",
        );
        return;
      }
      if (item.quantidade > disponivel.quantidade) {
        Swal.fire({
          title: "Estoque insuficiente",
          html: `<b>${item.produto_nome}</b>: disponível <b>${disponivel.quantidade} un</b>, solicitado <b>${item.quantidade} un</b>.`,
          icon: "warning",
        });
        return;
      }
    }

    // ── 1. Vincular sub-cliente? ──────────────────────────────────────────────
    let subClienteId = null;
    let subClienteObj = null;

    if (subClientes.length > 0) {
      const inputOptions = {
        "": "— Nenhum —",
        ...Object.fromEntries(subClientes.map((s) => [s.id, s.nome])),
      };
      const { value: scId } = await Swal.fire({
        title: "Vincular a um cliente?",
        text: "Opcional — aparecerá no comprovante.",
        input: "select",
        inputOptions,
        showCancelButton: true,
        confirmButtonText: "Continuar →",
        cancelButtonText: "Pular",
        allowEnterKey: true,
      });
      if (scId && scId !== "") {
        subClienteId = Number(scId);
        subClienteObj = subClientes.find((s) => s.id === subClienteId) ?? null;
      }
    }

    // ── 2. Pagamento ──────────────────────────────────────────────────────────
    let pagamentosTemp = [];
    let pago = 0;

    while (pago < total) {
      const pagamento = await solicitarPagamento(arredondar(total - pago));
      if (!pagamento) break;
      pagamentosTemp.push(pagamento);
      pago = arredondar(pago + pagamento.valor);
      if (pago < total) {
        const { isConfirmed } = await Swal.fire({
          title: "Adicionar outro pagamento?",
          text: `Falta ${formatBRL(arredondar(total - pago))}`,
          showCancelButton: true,
          confirmButtonText: "Sim ↵",
          cancelButtonText: "Não",
          allowEnterKey: true,
        });
        if (!isConfirmed) break;
      }
    }
    if (pago < total) {
      Swal.fire("Aviso", "Valor pago insuficiente.", "warning");
      return;
    }

    const troco = arredondar(pago - total);
    const itensFechados = [...itensVenda];
    const pgFechados = [...pagamentosTemp];
    const descontoFechado = descontoNum;

    try {
      await api.post("/venda", {
        itens: itensVenda.map((i) => ({
          produto_id: i.produto_id,
          quantidade: i.quantidade,
          tipo: i._tipo,
        })),
        pagamentos: pagamentosTemp,
        desconto: descontoNum,
        sub_cliente_id: subClienteId,
      });

      setEstoque((prev) =>
        prev.map((p) => {
          const v = itensFechados.find(
            (i) => i._tipo === "produto" && i.estoque_id === p.id,
          );
          return v ? { ...p, quantidade: p.quantidade - v.quantidade } : p;
        }),
      );

      setItensVenda([]);
      setDesconto("");
      try {
        localStorage.removeItem("pdv_itens");
        localStorage.removeItem("pdv_desconto");
      } catch {}

      await Swal.fire({
        title: "✓ Venda concluída!",
        html: `
          <div style="text-align:left;font-size:14px;line-height:2.2">
            ${subClienteObj ? `<div style="padding:8px 12px;background:rgba(26,86,219,.08);border-radius:8px;margin-bottom:12px;font-size:13px;color:#1A56DB;font-weight:600">👤 ${subClienteObj.nome}</div>` : ""}
            <div style="display:flex;justify-content:space-between"><span>Total</span><b>${formatBRL(total)}</b></div>
            <div style="display:flex;justify-content:space-between"><span>Pago</span><b>${formatBRL(pago)}</b></div>
            ${troco > 0 ? `<div style="display:flex;justify-content:space-between"><span>Troco</span><b style="color:var(--color-success)">${formatBRL(troco)}</b></div>` : ""}
            <hr style="margin:8px 0;opacity:.15"/>
            ${pagamentosTemp.map((p) => `<div style="display:flex;justify-content:space-between;font-size:13px"><span style="color:var(--color-text-muted)">${p.forma}</span><span>${formatBRL(p.valor)}</span></div>`).join("")}
          </div>
          <button id="btnComprovante" style="margin-top:14px;width:100%;height:42px;background:var(--color-btn-save-bg,#1A56DB);color:#fff;border:none;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>
            </svg>
            Gerar comprovante PDF
          </button>`,
        showConfirmButton: false,
        showCancelButton: true,
        cancelButtonText: "Fechar",
        allowEnterKey: false,
        didRender: () => {
          const btn = document.querySelector("#btnComprovante");
          if (!btn) return;
          const handler = async () => {
            Swal.close();
            await gerarComprovante(
              pgFechados,
              itensFechados,
              troco,
              descontoFechado,
              subClienteObj,
            );
          };
          btn.addEventListener("click", handler);
          btn.addEventListener("keydown", (e) => {
            if (e.key === "Enter") handler();
          });
          setTimeout(() => btn.focus(), 80);
        },
      });

      setTimeout(() => searchInputRef.current?.focus(), 200);
    } catch (err) {
      console.error("[Venda] finalizar:", err);
      Swal.fire(
        "Erro",
        err.response?.data?.error ?? "Não foi possível concluir a venda.",
        "error",
      );
    }
  }, [
    itensVenda,
    estoque,
    subClientes,
    total,
    descontoNum,
    solicitarPagamento,
    gerarComprovante,
  ]);

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="pdv">
      <AtalhosBar />

      <div className="pdv__body">
        <aside className="pdv__aside">
          {/* Busca */}
          <div className="pdv-search-card">
            <div className="pdv-search-card__labelrow">
              <label htmlFor="pdv-search" className="pdv-search-card__label">
                Produto ou Serviço
              </label>
              <Kbd>F2</Kbd>
            </div>
            <div className="pdv-search-wrap" ref={searchWrapRef}>
              <div className="pdv-search-field">
                <svg
                  className="pdv-search-field__icon"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <input
                  id="pdv-search"
                  ref={searchInputRef}
                  type="search"
                  className="pdv-search-field__input"
                  placeholder="Nome, EAN ou serviço…"
                  value={search}
                  autoComplete="off"
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setDropdown(e.target.value.trim().length > 0);
                    setSugestaoFocada(-1);
                  }}
                  onFocus={() => search.trim() && setDropdown(true)}
                  onKeyDown={handleSearchKeyDown}
                  aria-label="Buscar produto ou serviço"
                  aria-autocomplete="list"
                  aria-expanded={dropdownOpen}
                  aria-controls="sugestoes-lista"
                />
                {search && (
                  <button
                    type="button"
                    className="pdv-search-field__clear"
                    tabIndex={-1}
                    onClick={() => {
                      setSearch("");
                      setDropdown(false);
                      setSugestaoFocada(-1);
                      searchInputRef.current?.focus();
                    }}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>
              {dropdownOpen && (
                <div
                  id="sugestoes-lista"
                  className="pdv-sugestoes"
                  ref={dropdownRef}
                  role="listbox"
                >
                  {sugestoes.length > 0 ? (
                    sugestoes.map((item, i) => (
                      <SugestaoItem
                        key={item._key}
                        item={item}
                        onAdd={adicionarItem}
                        isFocused={sugestaoFocada === i}
                        itemRef={(el) => {
                          sugestaoRefs.current[i] = el;
                        }}
                        onKeyDown={(e) => handleSugestaoKeyDown(e, i)}
                        mostrarImagem={mostrarImagens}
                      />
                    ))
                  ) : (
                    <p className="pdv-sugestoes__vazio">
                      Nenhum produto ou serviço encontrado.
                    </p>
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              className="btn-buscar-todos"
              onClick={abrirBuscaCompleta}
              title="Ver todos (F10)"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              Ver todos <Kbd>F10</Kbd>
            </button>
          </div>

          {/* Toggle ocultar valores no comprovante */}
          {/* Toggle mostrar imagens */}
          <button
            type="button"
            className={`btn-toggle-valores${mostrarImagens ? " btn-toggle-valores--ativo" : ""}`}
            onClick={() => setMostrarImagens((v) => !v)}
            title={
              mostrarImagens
                ? "Ocultar imagens"
                : "Mostrar imagens dos produtos"
            }
          >
            {mostrarImagens ? (
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
            ) : (
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
            )}
            {mostrarImagens ? "Ocultar imagens" : "Mostrar imagens"}
          </button>

          {/* Totais */}
          <div className="pdv-totais" aria-label="Resumo da venda">
            <div className="pdv-totais__row">
              <span>Subtotal</span>
              <span>{formatBRL(subtotal)}</span>
            </div>
            {podeDesconto && (
              <div className="pdv-totais__row pdv-totais__row--desconto">
                <label
                  htmlFor="desconto"
                  className="pdv-totais__desconto-label"
                >
                  Desconto
                </label>
                <input
                  id="desconto"
                  type="number"
                  className="pdv-totais__desconto-input"
                  placeholder="0,00"
                  value={desconto}
                  min={0}
                  max={subtotal}
                  step="0.01"
                  onChange={(e) => setDesconto(e.target.value)}
                />
              </div>
            )}
            {descontoNum > 0 && (
              <div className="pdv-totais__row pdv-totais__row--danger">
                <span>Desconto</span>
                <span>− {formatBRL(descontoNum)}</span>
              </div>
            )}
            <div className="pdv-totais__total">
              <span>Total</span>
              <span>{formatBRL(total)}</span>
            </div>
          </div>

          {/* Ações */}
          <div className="pdv-actions">
            {itensVenda.length > 0 && (
              <button
                type="button"
                className="btn-limpar"
                onClick={limparVenda}
                title="Limpar (Delete)"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                </svg>
                Limpar venda <Kbd>Del</Kbd>
              </button>
            )}
            <button
              ref={finalizarBtnRef}
              type="button"
              className="btn-finalizar"
              onClick={finalizarVenda}
              disabled={!itensVenda.length || loading}
              title="Finalizar (F12)"
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Finalizar venda <Kbd>F12</Kbd>
            </button>
          </div>

          {cliente?.tipo_impressao && (
            <div className="pdv-impressora-info">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
              <span>
                {FORMATOS_LABEL[cliente.tipo_impressao] ??
                  cliente.tipo_impressao}
                {cliente.auto_imprimir && " · automático"}
              </span>
            </div>
          )}
        </aside>

        {/* Tabela */}
        <main className="pdv__main" aria-label="Itens da venda">
          <div className="pdv-itens-card">
            <div className="pdv-itens-card__header">
              <h2 className="pdv-itens-card__title">Itens da venda</h2>
              {itensVenda.length > 0 && (
                <div className="pdv-itens-card__meta">
                  <span className="pdv-itens-card__count" aria-live="polite">
                    {itensVenda.length} item{itensVenda.length !== 1 ? "s" : ""}
                  </span>
                  <span className="pdv-qty-hint" aria-hidden="true">
                    <Kbd>+</Kbd> add &nbsp;<Kbd>−</Kbd> red &nbsp;<Kbd>F3</Kbd>{" "}
                    editar
                  </span>
                </div>
              )}
            </div>
            <div
              className="pdv-table-wrapper"
              role="region"
              aria-label="Tabela de itens"
            >
              <table className="pdv-table">
                <thead>
                  <tr>
                    {mostrarImagens && <th scope="col">Foto</th>}
                    <th scope="col">EAN / Tipo</th>
                    <th scope="col">Produto / Serviço</th>
                    <th scope="col">Qtd</th>
                    <th scope="col">Unit.</th>
                    <th scope="col">Total</th>
                    <th scope="col" aria-label="Ações"></th>
                  </tr>
                </thead>
                <tbody>
                  {itensVenda.length === 0 ? (
                    <CarrinhoVazio />
                  ) : (
                    itensVenda.map((item) => {
                      const estoqueAtual = estoque.find(
                        (e) => e.id === item.estoque_id,
                      );
                      const maxQtd =
                        item._tipo === "servico"
                          ? Infinity
                          : (estoqueAtual?.quantidade ?? Infinity);
                      return (
                        <ItemRow
                          key={item.estoque_id}
                          item={{ ...item, _estoque_max: maxQtd }}
                          mostrarImagem={mostrarImagens}
                          onRemover={removerItem}
                          onAlterarQtd={alterarQuantidade}
                          onIncrementar={(id) => incrementarQtd(id, maxQtd)}
                          onDecrementar={decrementarQtd}
                          onDigitarQtd={(id) =>
                            abrirDialogoQtd(
                              id,
                              item.produto_nome,
                              item.quantidade,
                              maxQtd,
                            )
                          }
                          qtyRef={(el) => {
                            if (el) {
                              qtyRefs.current[item.estoque_id] = el;
                              el.addEventListener(
                                "focus",
                                () => {
                                  itemFocadoRef.current = item.estoque_id;
                                },
                                { once: false },
                              );
                            } else delete qtyRefs.current[item.estoque_id];
                          }}
                        />
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Venda;
